import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FiCheck } from "react-icons/fi";
import { useWorkshopSchedule } from "../hooks/useWorkshopSchedule";

// Special offer countdown - 40 minutes from page load
const OFFER_DURATION = 40 * 60 * 1000;

function useOfferCountdown() {
  const [timeLeft, setTimeLeft] = useState(() => {
    const savedEndTime = localStorage.getItem("offerEndTime");
    if (savedEndTime) {
      const endTime = parseInt(savedEndTime);
      const remaining = Math.max(0, endTime - Date.now());
      if (remaining > 0) {
        return getOfferTimeParts(remaining);
      }
    }
    const endTime = Date.now() + OFFER_DURATION;
    localStorage.setItem("offerEndTime", endTime.toString());
    return getOfferTimeParts(OFFER_DURATION);
  });

  useEffect(() => {
    const timer = setInterval(() => {
      const savedEndTime = localStorage.getItem("offerEndTime");
      if (savedEndTime) {
        const endTime = parseInt(savedEndTime);
        const remaining = Math.max(0, endTime - Date.now());
        if (remaining === 0) {
          const newEndTime = Date.now() + OFFER_DURATION;
          localStorage.setItem("offerEndTime", newEndTime.toString());
          setTimeLeft(getOfferTimeParts(OFFER_DURATION));
        } else {
          setTimeLeft(getOfferTimeParts(remaining));
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return timeLeft;
}

function getOfferTimeParts(milliseconds) {
  const seconds = Math.floor((milliseconds / 1000) % 60);
  const minutes = Math.floor((milliseconds / 1000 / 60) % 60);
  const hours = Math.floor((milliseconds / (1000 * 60 * 60)) % 24);
  const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));

  return { days, hours, minutes, seconds };
}

export default function HeroSection({ openRegistration = () => {} }) {
  const { workshopDetails } = useWorkshopSchedule();
  const offerTimeLeft = useOfferCountdown();

  const handleRegisterClick = () => {
    openRegistration();
  };

  return (
    <header className="hero-container">
      {/* Top thin bar */}
      <div className="hero-top-bar">
        🚀 Attention Healers: Feeling drained? Income unstable? Build systems and regain your freedom!
      </div>

      <section className="hero-section">
        <div className="hero-inner">
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="hero-headline-area"
          >
            <motion.h1
              className="hero-title"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              "Healing Others, But Income Still Unstable?"
              <br />
              <span className="highlight">
                Feeling drained after helping everyone? Ungal energy ellam others ku pogudhaa?
              </span>
              <br />
              <span className="subtitle-text">
                Turn Your Healing Gift into a Scalable Mission with AI — Get Predictable Income & Time Freedom (No Tech Skills Needed)
              </span>
            </motion.h1>

            <motion.p
              style={{
                fontSize: "clamp(1rem, 2.5vw, 1.2rem)",
                color: "#F2C94C",
                marginBottom: "2rem",
                lineHeight: "1.6",
                textAlign: "center",
                fontWeight: "600",
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              Stop Emotional Burnout. Build a Healing Practice That Runs on Autopilot.
            </motion.p>
          </motion.div>

          <div className="hero-columns">
            {/* VIDEO SECTION */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="hero-left-column"
            >
              <motion.div
                className="hero-image-card"
                whileHover={{ scale: 1.02 }}
                style={{
                  overflow: "hidden",
                  aspectRatio: "1 / 1",
                  backgroundColor: "#000",
                }}
              >
                <video
                  src="https://storage.googleapis.com/msgsndr/Te8QGpu12zbMxZIVQlZi/media/696c57d1b34b6424527920ae.mp4"
                  poster="https://storage.files-vault.com/uploads/1760945103-H0PweDnHcx.webp"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  style={{
                    display: "block",
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              </motion.div>
            </motion.div>

            {/* RIGHT: Offer Panel */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="hero-right-column"
            >
              <div className="offer-panel shine-animation">
                <motion.div
                  className="offer-header"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <FiCheck className="offer-check" />
                  <span>
                    Join the LIVE Workshop &<br />
                    Automate Your Healing Practice Today! ₹49
                  </span>
                </motion.div>

                <div className="offer-expiry">
                  <div className="expiry-text">
                    Limited Seats — Register Before the Clock Hits Zero!
                  </div>
                  <motion.div
                    className="countdown-display"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                  >
                    {[
                      { label: "Days", value: offerTimeLeft.days },
                      { label: "Hours", value: offerTimeLeft.hours },
                      { label: "Minutes", value: offerTimeLeft.minutes },
                      { label: "Seconds", value: offerTimeLeft.seconds, pulse: true },
                    ].map((unit, idx) => (
                      <div className="countdown-unit" key={idx}>
                        <motion.div
                          className={`countdown-number ${unit.pulse ? 'pulse-animation' : ''}`}
                          key={unit.value}
                          initial={{ scale: 1.2 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.3 }}
                        >
                          {String(unit.value || 0).padStart(2, "0")}
                        </motion.div>
                        <div className="countdown-label">{unit.label}</div>
                      </div>
                    ))}
                  </motion.div>
                </div>

                <motion.div
                  className="offer-time"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 1 }}
                >
                  Next Batch: {workshopDetails.formattedDate} — {workshopDetails.timeString}
                </motion.div>

                <motion.button
                  onClick={handleRegisterClick}
                  className="offer-cta"
                  aria-haspopup="dialog"
                  aria-label="Register for the workshop"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.2 }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  YES! I WANT TO JOIN FOR ₹49 🚀
                </motion.button>

                <motion.div
                  className="offer-bonus"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 1.4 }}
                >
                  <div className="bonus-text">
                    <span className="flame-icon">🔥</span>
                    Hurry! Only 50 Seats Available!
                  </div>
                  <div className="progress-container">
                    <div className="progress-bar">
                      <motion.div
                        className="progress-fill"
                        initial={{ width: "0%" }}
                        animate={{ width: "75%" }}
                        transition={{ duration: 2, delay: 1.6 }}
                      ></motion.div>
                    </div>
                    <div className="progress-text">
                      75% Seats Booked • AI Systems + Templates Included
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </header>
  );
}